import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { isBceLengthValid } from '../../utils/bce';
import useEnterprise from '../../hook/useEnterprise';
import useFormIframe from '../../hook/useFormIframe';
import usePartners from '../../hook/usePartners';
import './SurveyStartPage.css';
import Loading from '../../components/Loading/Loading';
import SurveyStepper from '../../components/SurveyStepper/SurveyStepper';
import StepBce from '../../components/SurveyStepper/StepBce';
import StepSector from '../../components/SurveyStepper/StepSector';
import StepShare from '../../components/SurveyStepper/StepShare';
import surveyTitles from '../../constants/surveyTitles';
import { createSurveySession, getSectorsByBce, getSurveySession } from '../../api/digiscore-api';

import { encodeBase64Json } from '../../utils/encodeBase64Json';
import { normalizeDigits } from '../../utils/normalizeDigits';
import { useSurveyEditId } from '../../hook/useSurveyEditId';
import { useIframeMessaging } from '../../hook/useIframeMessaging';

import { findRemoteDraftByBce } from '../../utils/findRemoteDraftByBce';

const INITIAL_TITLE_TOP = 265;
const STEPS = 3;
const STEP = { BCE: 0, SECTOR: 1, PARTNERS: 2 };
const IFRAME_BASE = '/Form/form.html';

function SurveyStartPage(props) {
  const { t, i18n } = useTranslation();
  const iframeRef = useRef(null);
  const bceInputRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedCustomSector, setSelectedCustomSector] = useState('')
  const [displayForm, setDisplayForm] = useState(false);
  const [iframeSrc, setIframeSrc] = useState(IFRAME_BASE);
  const [fallbackSectors, setFallbackSectors] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupDraftId, setDupDraftId] = useState(null);
  const [dupEnterpriseName, setDupEnterpriseName] = useState(null);

  const { getAccessTokenSilently } = useAuth0();

  const { user } = useAuth0();
  const { postPrefill, onLoadOnce } = useIframeMessaging(iframeRef);
  const { editId, isEdit } = useSurveyEditId();

  const API_BASE = useMemo(
    () => (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, ''),
    []
  );

  const { partners, togglePartner } = usePartners({
    token: props.token,
    shouldFetch: !displayForm && activeStep === STEP.PARTNERS,
    onBusyChange: setLoading
  });

  // 👉 enterpriseName vient de ton hook useEnterprise
  const { enterpriseName, sectors, fetchEnterprise, address: enterpriseAddress } = useEnterprise({ t, token: props.token });


  const isBceValid = useMemo(() => isBceLengthValid(inputValue), [inputValue]);
  const sectorsToShow = fallbackSectors || sectors;

  const postConfig = useCallback(() => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'DIGISCORE_CONFIG', token: props.token || null, apiBase: API_BASE },
        '*'
      );
    } catch (e) {
      console.warn('[SurveyStart] postConfig failed', e);
    }
  }, [props.token, API_BASE]);

  useEffect(() => {
    if (loadingMessage === 'survey.create.stepper.checkBce') {
      setLoadingMessage(t('survey.create.stepper.checkBce'));
    }
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    if (!isEdit) return;

    setLoading(true);
    let cleanup = () => {};
    (async () => {
      try {
        const session = await getSurveySession(editId, props.token);
        const payload = session?.Draft?.answers ?? session?.Draft ?? null;

        const pf = payload ? encodeBase64Json(payload) : '';
        const src = `${IFRAME_BASE}?mode=edit&id=${encodeURIComponent(
          editId
        )}${pf ? `&pf=${encodeURIComponent(pf)}` : ''}&ts=${Date.now()}`;

        setIframeSrc(src);
        setDisplayForm(true);
        setLoadingMessage(t('survey.create.stepper.preFillForm'));

        cleanup = onLoadOnce(() => {
          if (payload) postPrefill(payload);
          postConfig();
        });
      } catch (e) {
        console.error('[SurveyStart] edit prefill (DB) failed, continuing without pf:', e);
        const src = `${IFRAME_BASE}?mode=edit&id=${encodeURIComponent(editId)}&ts=${Date.now()}`;
        setIframeSrc(src);
        setDisplayForm(true);

        cleanup = onLoadOnce(() => {
          postConfig();
        });
      } finally {}
    })();

    return () => cleanup();
    // eslint-disable-next-line
  }, [isEdit, editId, onLoadOnce, postPrefill, postConfig, props.token]);

  useEffect(() => {
    const onMsg = (evt) => {
      const d = evt?.data;
      if (!d) return;

      if (d.type === 'DIGISCORE_LOADING') {
        setLoading(!!d.payload?.on);
        if (typeof d.payload?.message === 'string') {
          setLoadingMessage(d.payload.message);
        }
      }

      if (d.type === 'DIGISCORE_REDIRECT') {
        const to = d.payload?.to || '/survey';
        window.location.assign(to);
      }

      if (d.type === 'DIGISCORE_IFRAME_READY') {
        setLoading(false);
        setLoadingMessage('');
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const send = () => {
      try {
        iframe.contentWindow?.postMessage(
          { type: 'DIGISCORE_CONFIG', token: props.token || null, apiBase: API_BASE },
          '*'
        );
      } catch (e) {
        console.log('[SurveyStart] postMessage failed', e);
      }
    };

    iframe.addEventListener('load', send);
    const t1 = setTimeout(send, 300);
    const t2 = setTimeout(send, 1000);

    return () => {
      iframe.removeEventListener('load', send);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [iframeRef, props?.token, API_BASE, iframeSrc]);

  const handleFirstStepNext = useCallback(async () => {
    const digits = normalizeDigits(inputValue);
    if (!isBceLengthValid(digits)) return;
    setLoadingMessage(t('survey.create.stepper.checkBce'));
    setLoading(true);
    setFallbackSectors(null);
    try {
      const found = await findRemoteDraftByBce(digits, user, props.token);
      if (found) {
        setDupDraftId(found.draftId);
        setDupEnterpriseName(found.enterpriseName);
        setDupOpen(true);
        return;
      }
      let ok = await fetchEnterprise(digits);
      if (!ok) {
        try {
          const alt = await getSectorsByBce(digits, props.token);
          if (Array.isArray(alt) && alt.length > 0) {
            setFallbackSectors(alt);
            ok = true;
          }
        } catch (e) {
          console.log('[SurveyStart] fallback getSectorsByBce error:', e);
        }
      }

      if (ok) {
        setActiveStep((s) => Math.min(s + 1, STEPS - 1));
      }
    } catch (e) {
      console.log('[SurveyStart] handleFirstStepNext error:', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [inputValue, user, fetchEnterprise, props.token]);

  const handleResumeDraft = useCallback(() => {
    if (dupDraftId) {
      window.location.assign(`/survey/${dupDraftId}`);
    }
  }, [dupDraftId]);

  const handleChooseOtherBce = useCallback(() => {
    setDupOpen(false);
    setInputValue('');
    setTimeout(() => bceInputRef.current?.focus(), 0);
  }, []);

  const handleChange = useCallback((e) => {
    setInputValue(normalizeDigits(e.target.value));
  }, []);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain');
    setInputValue(normalizeDigits(pasted));
  }, []);

  const handleSectorChange = (e) => {
    const value = e.target.value

    // si on clique sur "Autre"
    if (value === 'other') {
      setSelectedSector('other')
      return
    }

    // si on clique un secteur normal
    setSelectedCustomSector('')
    setSelectedSector(value)
  }

  const handleCustomSectorChange = (e) => {
    const value = e.target.value

    // Toujours garder "Autre" sélectionné dans les radios
    setSelectedSector('other')

    // Enregistrer le vrai code secteur choisi
    setSelectedCustomSector(value)
  }

  const isSectorValid =
    selectedSector !== null &&
    (selectedSector !== 'other' || selectedCustomSector !== '')

  const finalSectorCode =
    selectedSector === 'other' ? selectedCustomSector : selectedSector

  const storeData = useCallback(async () => {
    setLoading(true);
    try {
      setLoadingMessage(t('survey.create.stepper.folderCreate'));

      const token = await getAccessTokenSilently();

      const payload = {
        bce: String(inputValue || '').replace(/\D+/g, ''),
        sectorCode: finalSectorCode,
        sharedWith: (partners || [])
          .filter(p => p?.checked || p?.selected)
          .map(p => p.id),
        user: { email: user.email, sub: user.sub },

        enterpriseName: enterpriseName || '',
        address: enterpriseAddress || {}
      };

      const created = await createSurveySession(payload, token);
      const newId = created?._id;
      if (!newId) throw new Error('SurveySession id missing');

      setLoadingMessage(t('survey.create.stepper.openForm'));
      const src = `${IFRAME_BASE}?mode=edit&id=${encodeURIComponent(newId)}&ts=${Date.now()}`;
      setIframeSrc(src);
      setDisplayForm(true);

      onLoadOnce(() => {
        postConfig();
      });
    } catch (e) {
      console.error('[storeData] createSurveySession failed:', e);
      setLoading(false);
      setLoadingMessage('');
    }
    // eslint-disable-next-line
  }, [
    inputValue,
    selectedSector,
    partners,
    user,
    enterpriseName,
    onLoadOnce,
    postConfig,
    getAccessTokenSilently
  ]);

  const guardedSetActiveStep = useCallback(
    (nextOrUpdater) => {
      setActiveStep((prev) => {
        const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
        if (prev === STEP.SECTOR && !selectedSector && next > prev) return prev;
        return next;
      });
    },
    [selectedSector]
  );

  const { currentTitle, iframeHeight } = useFormIframe({
    displayForm,
    titlesMap: surveyTitles,
    onProgressReady: () => {
      if (!isEdit) setLoading(false);
    }
  });

  return (
    <div className="survey">
      {loading && <Loading message={loadingMessage} />}

      {!displayForm ? (
        <Container className="survey__content" style={{ marginTop: '110px' }}>
          <SurveyStepper
            t={t}
            activeStep={activeStep}
            setActiveStep={guardedSetActiveStep}
            stepsCount={3}
            loading={loading}
            sectors={sectorsToShow}
            selectedSector={selectedSector}
            selectedCustomSector={selectedCustomSector}
            handleSectorChange={handleSectorChange}
            handleCustomSectorChange={handleCustomSectorChange}
            isBceValid={isBceValid}
            isSectorValid={isSectorValid}
            onFirstStepNext={handleFirstStepNext}
            advanceAfterFirstStep={false}
            onFinish={storeData}
            hideBackOnFirstStep={true}
          >
            {activeStep === 0 && (
              <StepBce
                t={t}
                inputValue={inputValue}
                handleChange={handleChange}
                handlePaste={handlePaste}
                inputRef={bceInputRef}
              />
            )}
            {activeStep === 1 && (
              <StepSector
                t={t}
                enterpriseName={enterpriseName}
                sectors={sectorsToShow}
                selectedSector={selectedSector}
                selectedCustomSector={selectedCustomSector}
                handleSectorChange={handleSectorChange}
                handleCustomSectorChange={handleCustomSectorChange}
              />
            )}

            {activeStep === 2 && (
              <StepShare
                t={t}
                partners={partners}
                handleCheckboxChange={togglePartner}
              />
            )}
          </SurveyStepper>
        </Container>
      ) : (
        <>
          <Typography
            variant="h6"
            id="title"
            style={{
              visibility: loading ? 'hidden' : 'visible',
              position: 'fixed',
              top: `${INITIAL_TITLE_TOP}px`,
              left: 0,
              maxWidth: '35%',
              marginLeft: '3.5em',
              backgroundColor: 'transparent',
              padding: '12px 0',
              textAlign: 'left',
              zIndex: 1000,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '36px',
              lineHeight: 1.1,
              color: '#3ba0dc',
              listStyle: 'none',
              wordBreak: 'break-word',
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: 'none'
            }}
          >
            {currentTitle}
          </Typography>

          <div
            style={{
              visibility: loading ? 'hidden' : 'visible',
              height: loading ? 0 : iframeHeight,
              overflow: 'hidden',
              paddingTop: '60px'
            }}
          >
            <iframe
              ref={iframeRef}
              key={iframeSrc}
              src={iframeSrc}
              title="Formulaire"
              style={{ width: '100%', height: iframeHeight, border: 'none', display: 'block' }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-modals"
            />
          </div>
        </>
      )}

      <Dialog
        open={dupOpen}
        onClose={() => setDupOpen(false)}
        aria-labelledby="dup-title"
        PaperProps={{
          sx: {
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle id="dup-title" sx={{ fontWeight: 800 }}>
          {t('survey.start.alreadyinProgress')}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          <div style={{ maxWidth: 520 }}>
            {dupEnterpriseName ? (
              <Typography sx={{ mb: 2, lineHeight: 1.5 }}>
                {t('survey.start.alreadyExists', { name: dupEnterpriseName })}
              </Typography>
            ) : (
              <Typography sx={{ mb: 2, lineHeight: 1.5 }}>
                {t('survey.start.alreadyExistsNoName')}
              </Typography>
            )}

            <Typography sx={{ mb: 1.5, lineHeight: 1.6 }}>
              {t('survey.start.cannotCreate')}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleChooseOtherBce}
            startIcon={<SwapHorizRoundedIcon />}
          >
            {t('survey.start.chooseOtherBce')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleResumeDraft}
            startIcon={<PlayCircleOutlineRoundedIcon />}
          >
            {t('survey.start.resumeDraft')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SurveyStartPage;
